import _defineProperty from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import useSWR from 'swr';
import { createContext, useContext, useCallback, useMemo } from 'react';
import { useUser } from '@poplar/hooks/useUser';
import { useAblyEvents } from '@poplar/hooks/useAblyEvents';
import { AblyResponseTypes, AccessType } from '@poplar/types/enums';
import { API_GUIDE, API_MEMBER_ACTIONS } from '@poplar/constants/routes';
import { fetcher } from '@yoweb/utils/common';
import { useEntitlement } from './EntitlementProvider';
import { convertTimeProtoToTimestamp } from '@yoweb/utils/converters';
import { isNumber } from '@yoweb/utils/isNumber';
import { jsx as _jsx } from "react/jsx-runtime";

var calculateCallState = function calculateCallState(timestamp) {
  var date = convertTimeProtoToTimestamp(timestamp);
  var threeWeeksAgo = new Date();
  threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);

  if (isNumber(date) && threeWeeksAgo.getTime() > date) {
    return 1;
  }

  return 0;
}; // A direct port of:
// https://github.com/yolabs-io/aspen/blob/3a26cad012f31098df361b6b5601fa1892f7cdfd/apps/oak/lib/src/bottom_nav/bloc/bottom_nav_bloc.dart#L323-L328


var calculateGuideBadgeCount = function calculateGuideBadgeCount(callState, hasActiveSubscription) {
  if (!callState || !hasActiveSubscription) {
    return 0;
  }

  if (callState.scheduleInitial) {
    return 1;
  }

  if (callState.initialCanceled) {
    return calculateCallState(callState.initialCanceled.created);
  }

  if (callState.initialCompleted) {
    return calculateCallState(callState.initialCompleted.created);
  }

  if (callState.scheduleMonthly) {
    return calculateCallState(callState.scheduleMonthly.created);
  }

  if (callState.monthlyCanceled) {
    return calculateCallState(callState.monthlyCanceled.created);
  }

  if (callState.monthlyCompleted) {
    return calculateCallState(callState.monthlyCompleted.created);
  }

  return 0;
};

export var MemberActionsContext = /*#__PURE__*/createContext(undefined);
var commonSwrConfig = {
  refreshInterval: 1000 * 60 * 5,
  // 5 mins
  revalidateOnFocus: false,
  shouldRetryOnError: false
};
export var MemberActionsProvider = function MemberActionsProvider(_ref) {
  var _user$profile, _useAblyEvents, _guideInfo$guideInter, _guideInfo$guideInter2;

  var children = _ref.children;
  var user = useUser();

  var _useEntitlement = useEntitlement(),
      accessType = _useEntitlement.accessType;

  var _useSWR = useSWR(user.isAuthenticated && user !== null && user !== void 0 && (_user$profile = user.profile) !== null && _user$profile !== void 0 && _user$profile.assignedRepId ? API_GUIDE : undefined, fetcher, commonSwrConfig),
      guideInfo = _useSWR.data,
      refetchGuideInfo = _useSWR.mutate;

  var _useSWR2 = useSWR(user.isAuthenticated ? API_MEMBER_ACTIONS : undefined, fetcher, _objectSpread({}, commonSwrConfig)),
      memberActions = _useSWR2.data,
      refreshMemberActions = _useSWR2.mutate;

  var handleMemberActionsRelatedEvent = useCallback(function () {
    return void refreshMemberActions();
  }, [refreshMemberActions]);
  var handleGuideRelatedEvent = useCallback(function () {
    return void refetchGuideInfo();
  }, [refetchGuideInfo]);
  useAblyEvents((_useAblyEvents = {}, _defineProperty(_useAblyEvents, AblyResponseTypes.NOTIFICATION_TYPE_COMMENT_ADDED, handleMemberActionsRelatedEvent), _defineProperty(_useAblyEvents, AblyResponseTypes.NOTIFICATION_TYPE_TASK_CHANGED_V2, handleMemberActionsRelatedEvent), _defineProperty(_useAblyEvents, AblyResponseTypes.NOTIFICATION_TYPE_TASK_PROPOSAL_ELECTED, handleMemberActionsRelatedEvent), _defineProperty(_useAblyEvents, AblyResponseTypes.NOTIFICATION_TYPE_GUIDE_INTERACTION_UPDATE, handleGuideRelatedEvent), _useAblyEvents), 'all');
  var guideNotificationCount = calculateGuideBadgeCount(guideInfo === null || guideInfo === void 0 ? void 0 : (_guideInfo$guideInter = guideInfo.guideInteraction) === null || _guideInfo$guideInter === void 0 ? void 0 : (_guideInfo$guideInter2 = _guideInfo$guideInter.interaction) === null || _guideInfo$guideInter2 === void 0 ? void 0 : _guideInfo$guideInter2.guideCallState, accessType === AccessType.LIMITED || accessType === AccessType.FULL);
  var state = useMemo(function () {
    return {
      guideNotificationCount: guideNotificationCount,
      actionItems: memberActions !== null && memberActions !== void 0 ? memberActions : []
    };
  }, [guideNotificationCount, memberActions]);
  var actions = useMemo(function () {
    return {
      refreshMemberActions: refreshMemberActions
    };
  }, [refreshMemberActions]);
  var value = useMemo(function () {
    return {
      state: state,
      actions: actions
    };
  }, [state, actions]);
  return /*#__PURE__*/_jsx(MemberActionsContext.Provider, {
    value: value,
    children: children
  });
};
export var useMemberActions = function useMemberActions() {
  var context = useContext(MemberActionsContext);

  if (context === undefined) {
    throw new Error('useMemberActions must be used within a MemberActionsProvider');
  }

  return context;
};